import React from "react";
import t from "../../../../../../i18n";
import formatDate from "@ui/i18n/formatDate";
import type { Options } from "@ui/Highcharts";
import { useQOI } from "../../../useQOI";
import { formatPeriodAxis, useReportingPeriodAxis } from "../../../useReportingPeriodAxis";
import { useReportingTooltipRender } from "../../../useReportingTooltipRender";
import { colors } from "@ui/Theme";
import { useReparabilityMatrixPeriod } from "../useReparabilityMatrixPeriod";
import validateInterface from "@ui/lib/validateInterface";
import { ReportingPointMetadata } from "../../../../../Project/Chart/PointMetadata";
import type { SeriesColumnOptions } from "highcharts";
import { getColumnNavigatorSeries } from "../../../utils/navigator";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { DiagnosisReporting, ReportingPeriod } from "../../../../../../api/useReporting";

interface OptionParamerters {
  reporting: DiagnosisReporting;
  reportingPeriod: ReportingPeriod | null;
  series: Highcharts.SeriesOptionsType[];
}

export function useOptions(
  chart: React.RefObject<Highcharts.Chart>,
  { reporting, reportingPeriod, series }: OptionParamerters,
) {
  const translations = useTranslations();
  const [qoi] = useQOI(reporting);
  const initialXAxis = useReportingPeriodAxis(chart, reporting, reportingPeriod);
  const [_, setReparabilityMatrixPeriod] = useReparabilityMatrixPeriod({});

  const renderTooltip = useReportingTooltipRender();

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const points = this.points ?? [];

      const { projectCount, filledCount } = validateInterface(
        ReportingPointMetadata,
        points[0]?.point.custom,
      );

      const serieValues = points.map(p => ({
        name: p.series.name,
        color: p.color as string,
        value: p.y,
        unit: qoi?.unit ?? "",
      }));

      const [totalLosses, unknownLosses, ...series] = serieValues;
      const orderedSeries = [totalLosses, ...series, unknownLosses].filter(
        (x): x is NonNullable<{ name: string; color: string; value: number; unit: string }> =>
          Boolean(x),
      );

      const projectPercent =
        (projectCount / (reporting ? Object.keys(reporting.projectById).length : 1)) * 100;

      return renderTooltip({
        title: formatDate(new Date(this.x), "month-date", null),
        subtitle: t(
          "reporting.fleet_performance.tooltip.project_subtitle",
          projectCount,
          filledCount ?? 0,
          Number(projectPercent.toFixed(1)),
        ),
        values: [orderedSeries],
      });
    },
    [qoi?.unit, renderTooltip, reporting],
  );

  const onColumnMouseOver = React.useCallback(function (
    this: Highcharts.Series & { barW?: number },
  ) {
    const scale = 3;
    this.chart.xAxis[0]?.update({
      crosshair: {
        width: ((this.barW ?? 1) / scale) * scale,
      },
    });
    this.chart.update({ plotOptions: { series: { states: { inactive: { enabled: false } } } } });
  }, []);

  const onColumnMouseOut = React.useCallback(function (this: Highcharts.Series) {
    this.chart.update({
      plotOptions: {
        series: {
          states: {
            inactive: {
              enabled: true,
            },
          },
        },
      },
    });
  }, []);

  const onColumnClick = React.useCallback(
    function (this: Highcharts.Point) {
      setReparabilityMatrixPeriod(this.x);
    },
    [setReparabilityMatrixPeriod],
  );

  return React.useMemo(
    (): Options | null =>
      reporting
        ? {
            initial: {
              xAxis: initialXAxis,
            },
            chart: {
              ignoreHiddenSeries: true,
              zoomType: "x",
              marginTop: 5,
              type: "column",
              animation: true,
            },
            tooltip: {
              borderColor: colors.raw.titanium500,
              shared: true,
              formatter: constantTooltipFormatter,
            },
            series,
            plotOptions: {
              series: {
                zoneAxis: "x",
              },
              column: {
                cursor: "pointer",
                point: {
                  events: {
                    click: onColumnClick,
                  },
                },
                events: {
                  mouseOver: onColumnMouseOver,
                  mouseOut: onColumnMouseOut,
                },
              },
            },
            xAxis: {
              crosshair: {
                color: colors.raw.carbon200,
              },
            },
            yAxis: {
              id: "diagnosis-internal-losses",
              minPadding: 0.1,
              title: {
                text: t("reporting.fleet_performance.chart.internal_losses", translations),
                textAlign: "center",
                useHTML: true,
              },
              labels: {
                unit: qoi?.unit ?? "",
                sign: false,
                useHTML: true,
              },
            },
            navigator: {
              enabled: true,
              xAxis: { labels: { formatter: formatPeriodAxis } },
              /**
               * @note
               * This is a workaround to fix the navigator column serie size.
               * We won't use replicateInNavigator options on series because we
               * don't have any way to specify columns width from the series to the navigator.
               * When the series contains a lot of data, there is no any issue on bar size,
               * but on low data, the bar size is too big, making series overlaping.
               */
              series: getColumnNavigatorSeries(series as SeriesColumnOptions[]),
            },
          }
        : null,
    [
      constantTooltipFormatter,
      initialXAxis,
      onColumnClick,
      onColumnMouseOut,
      onColumnMouseOver,
      qoi?.unit,
      reporting,
      series,
      translations,
    ],
  );
}
